<script>
import { useAlert } from 'dashboard/composables';
import { mapActions } from 'vuex';
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { apiErrorMessage } from '../../../../api/coLiving/errors';
import { FIELD_CLASS, SELECT_CLASS } from '../constants';
import {
  SEVERITIES,
  SLA_TIERS,
  VISIT_OFFERS,
  VISIT_MINUTES_MIN,
  VISIT_MINUTES_MAX,
  emptyCategory,
  categoryToForm,
  validateCategory,
  categoryPayload,
} from '../catalogForms';
import FormField from './FormField.vue';

export default {
  name: 'CategoryModal',
  components: { BaseModal, FormField },
  props: {
    category: { type: Object, default: null },
  },
  emits: ['close'],
  data() {
    return {
      fieldClass: FIELD_CLASS,
      selectClass: SELECT_CLASS,
      severities: SEVERITIES,
      tiers: SLA_TIERS,
      visitOffers: VISIT_OFFERS,
      visitMin: VISIT_MINUTES_MIN,
      visitMax: VISIT_MINUTES_MAX,
      form: this.category ? categoryToForm(this.category) : emptyCategory(),
      errors: {},
      isSubmitting: false,
    };
  },
  computed: {
    isEdit() {
      return !!this.category;
    },
  },
  methods: {
    ...mapActions('coLivingCatalog', ['addCategory', 'editCategory']),
    errorText(field) {
      return this.errors[field]
        ? this.$t(`CO_LIVING.FORM_ERRORS.${this.errors[field]}`, {
            min: this.visitMin,
            max: this.visitMax,
          })
        : '';
    },
    async onSubmit() {
      this.errors = validateCategory(this.form, { isEdit: this.isEdit });
      if (Object.keys(this.errors).length) return;
      this.isSubmitting = true;
      const body = categoryPayload(this.form, { isEdit: this.isEdit });
      try {
        if (this.isEdit) {
          await this.editCategory({ key: this.category.key, fields: body });
        } else {
          await this.addCategory(body);
        }
        useAlert(this.$t('CO_LIVING.CATEGORIES.SAVED'));
        this.$emit('close');
      } catch (err) {
        useAlert(
          apiErrorMessage(this, err, 'CO_LIVING.CATEGORIES.SAVE_FAILED')
        );
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <BaseModal size="large" @close="$emit('close')">
    <template #title>
      {{
        isEdit
          ? $t('CO_LIVING.CATEGORIES.EDIT_CATEGORY')
          : $t('CO_LIVING.CATEGORIES.ADD_CATEGORY')
      }}
    </template>

    <form
      class="grid grid-cols-1 md:grid-cols-2 gap-4"
      data-testid="co-living-category-form"
      @submit.prevent="onSubmit"
    >
      <FormField
        :label="$t('CO_LIVING.CATEGORIES.FIELDS.KEY')"
        for-id="co-living-category-key"
        :error="errorText('key')"
        :hint="$t('CO_LIVING.CATEGORIES.FIELDS.KEY_HINT')"
      >
        <input
          id="co-living-category-key"
          v-model="form.key"
          type="text"
          maxlength="64"
          :disabled="isEdit"
          :class="[fieldClass, errors.key ? 'border-red-500' : '']"
          data-testid="co-living-category-key"
        />
      </FormField>

      <FormField
        :label="$t('CO_LIVING.CATEGORIES.FIELDS.NAME')"
        for-id="co-living-category-name"
        :error="errorText('name')"
      >
        <input
          id="co-living-category-name"
          v-model="form.name"
          type="text"
          maxlength="120"
          :class="[fieldClass, errors.name ? 'border-red-500' : '']"
          data-testid="co-living-category-name"
        />
      </FormField>

      <div class="md:col-span-2">
        <FormField
          :label="$t('CO_LIVING.CATEGORIES.FIELDS.DESCRIPTION')"
          for-id="co-living-category-description"
        >
          <input
            id="co-living-category-description"
            v-model="form.description"
            type="text"
            :class="fieldClass"
            data-testid="co-living-category-description"
          />
        </FormField>
      </div>

      <FormField
        :label="$t('CO_LIVING.CATEGORIES.FIELDS.SEVERITY')"
        for-id="co-living-category-severity"
      >
        <select
          id="co-living-category-severity"
          v-model="form.severity"
          :class="selectClass"
          data-testid="co-living-category-severity"
        >
          <option v-for="s in severities" :key="s" :value="s">
            {{ $t(`CO_LIVING.CATEGORIES.SEVERITIES.${s.toUpperCase()}`) }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.CATEGORIES.FIELDS.SLA_TIER')"
        for-id="co-living-category-tier"
        :hint="$t('CO_LIVING.CATEGORIES.FIELDS.SLA_TIER_HINT')"
      >
        <select
          id="co-living-category-tier"
          v-model="form.sla_tier"
          :class="selectClass"
          data-testid="co-living-category-tier"
        >
          <option v-for="tier in tiers" :key="tier" :value="tier">
            {{ tier }} {{ $t(`CO_LIVING.SLA.TIER_NAMES.${tier}`) }}
          </option>
        </select>
      </FormField>

      <FormField
        :label="$t('CO_LIVING.CATEGORIES.FIELDS.VISIT_MINUTES')"
        for-id="co-living-category-visit-minutes"
        :error="errorText('visit_minutes')"
        :hint="$t('CO_LIVING.CATEGORIES.FIELDS.VISIT_MINUTES_HINT')"
      >
        <input
          id="co-living-category-visit-minutes"
          v-model="form.visit_minutes"
          type="number"
          :min="visitMin"
          :max="visitMax"
          :class="[fieldClass, errors.visit_minutes ? 'border-red-500' : '']"
          data-testid="co-living-category-visit-minutes"
        />
      </FormField>

      <FormField
        :label="$t('CO_LIVING.CATEGORIES.FIELDS.VISIT_OFFER')"
        for-id="co-living-category-visit-offer"
        :hint="$t('CO_LIVING.CATEGORIES.FIELDS.VISIT_OFFER_HINT')"
      >
        <select
          id="co-living-category-visit-offer"
          v-model="form.visit_offer"
          :class="selectClass"
          data-testid="co-living-category-visit-offer"
        >
          <option v-for="offer in visitOffers" :key="offer" :value="offer">
            {{ $t(`CO_LIVING.CATEGORIES.VISIT_OFFERS.${offer.toUpperCase()}`) }}
          </option>
        </select>
      </FormField>

      <div class="md:col-span-2">
        <FormField
          :label="$t('CO_LIVING.CATEGORIES.FIELDS.PLAYBOOK')"
          for-id="co-living-category-playbook"
          :hint="$t('CO_LIVING.CATEGORIES.FIELDS.PLAYBOOK_HINT')"
        >
          <textarea
            id="co-living-category-playbook"
            v-model="form.playbook"
            rows="6"
            :class="fieldClass"
            data-testid="co-living-category-playbook"
          />
        </FormField>
      </div>

      <label
        class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
      >
        <input
          v-model="form.emergency"
          type="checkbox"
          class="mb-0"
          data-testid="co-living-category-emergency"
        />
        {{ $t('CO_LIVING.CATEGORIES.FIELDS.EMERGENCY') }}
      </label>

      <label
        class="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200"
      >
        <input
          v-model="form.active"
          type="checkbox"
          class="mb-0"
          data-testid="co-living-category-active"
        />
        {{ $t('CO_LIVING.COMMON.ACTIVE') }}
      </label>

      <div class="md:col-span-2 flex justify-end gap-3">
        <woot-button variant="clear" type="button" @click="$emit('close')">
          {{ $t('CO_LIVING.COMMON.CANCEL') }}
        </woot-button>
        <woot-button
          type="submit"
          :is-loading="isSubmitting"
          data-testid="co-living-category-save"
        >
          {{ $t('CO_LIVING.COMMON.SAVE') }}
        </woot-button>
      </div>
    </form>
  </BaseModal>
</template>
